import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import {
  getGetInstagramConnectUrlQueryOptions,
  getGetStoreInstagramAccountQueryKey,
  useDisconnectInstagramAccount,
  useGetStoreInstagramAccount,
  useResubscribeInstagramWebhooks,
} from "@/api";
import { InstagramAccountStatus } from "@/api/schemas/instagramAccountStatus";
import {
  INSTAGRAM_BOT_ENABLED,
  INSTAGRAM_CONNECT_STEP_KEYS,
  INSTAGRAM_FEATURES,
  INSTAGRAM_PROFILE_BASE_URL,
  INSTAGRAM_REQUIREMENT_KEYS,
  INSTAGRAM_STATUS_META,
} from "@/lib/instagram/constants";
import { formatPersianDate } from "@/lib/format/persian";

export type InstagramConnectionState = "loading" | "disconnected" | "connected" | "attention";

/** Connect / status / disconnect for the Instagram account of one store. */
export function useInstagramConnection(storeId: number | null, enabled: boolean, canManage: boolean) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);

  const { data, isLoading } = useGetStoreInstagramAccount(storeId ?? 0, {
    query: { enabled: INSTAGRAM_BOT_ENABLED && enabled && storeId !== null },
  });
  const account = data?.status === 200 ? data.data.data : null;

  const refresh = useCallback(() => {
    if (storeId === null) return;
    void queryClient.invalidateQueries({ queryKey: getGetStoreInstagramAccountQueryKey(storeId) });
  }, [queryClient, storeId]);

  const { mutate: disconnect, isPending: isDisconnecting } = useDisconnectInstagramAccount({
    mutation: {
      onSettled: () => {
        setIsDisconnectOpen(false);
        refresh();
      },
    },
  });
  const { mutate: resubscribe, isPending: isResubscribing } = useResubscribeInstagramWebhooks({
    mutation: { onSettled: refresh },
  });

  // Instagram's consent screen must be opened with a fresh, short-lived signed link.
  const onConnect = useCallback(async () => {
    if (!INSTAGRAM_BOT_ENABLED) {
      void navigate({ to: "/coming-soon" });
      return;
    }
    if (storeId === null || !canManage) return;
    setIsRedirecting(true);
    try {
      const response = await queryClient.fetchQuery({
        ...getGetInstagramConnectUrlQueryOptions(storeId),
        staleTime: 0,
      });
      if (response.status === 200) {
        window.location.assign(response.data.data.url);
        return;
      }
      setIsRedirecting(false);
    } catch {
      setIsRedirecting(false);
    }
  }, [canManage, navigate, queryClient, storeId]);

  const state: InstagramConnectionState = useMemo(() => {
    if (isLoading) return "loading";
    if (!account) return "disconnected";
    return account.status === InstagramAccountStatus.active ? "connected" : "attention";
  }, [account, isLoading]);

  const accountView = useMemo(() => {
    if (!account) return null;
    const statusMeta = INSTAGRAM_STATUS_META[account.status];
    return {
      username: account.username,
      displayName: account.name || account.username,
      avatarUrl: account.profile_picture_url,
      profileUrl: `${INSTAGRAM_PROFILE_BASE_URL}${account.username}`,
      accountTypeKey: account.account_type ? `account.accountTypes.${account.account_type}` : null,
      statusLabelKey: statusMeta.labelKey,
      statusColor: statusMeta.color,
      attentionDescriptionKey:
        account.status === InstagramAccountStatus.revoked
          ? "account.revokedDescription"
          : "account.expiredDescription",
      eventsActive: account.webhook_subscribed,
      tokenExpiresLabel: formatPersianDate(new Date(account.token_expires_at)),
      connectedAtLabel: formatPersianDate(new Date(account.created_at)),
      scopeKeys: account.scopes.map((scope) => `account.scopes.${scope}`),
      lastError: account.last_error ?? null,
    };
  }, [account]);

  return {
    state,
    canManage,
    account: accountView,
    features: INSTAGRAM_FEATURES,
    stepKeys: INSTAGRAM_CONNECT_STEP_KEYS,
    requirementKeys: INSTAGRAM_REQUIREMENT_KEYS,
    isRedirecting,
    onConnect: () => void onConnect(),
    isResubscribing,
    onResubscribe: () => {
      if (storeId !== null) resubscribe({ storeId });
    },
    isDisconnectOpen,
    isDisconnecting,
    onDisconnectRequest: () => setIsDisconnectOpen(true),
    onDisconnectCancel: () => setIsDisconnectOpen(false),
    onDisconnectConfirm: () => {
      if (storeId !== null) disconnect({ storeId });
    },
  };
}

export type InstagramConnectionView = ReturnType<typeof useInstagramConnection>;
export type InstagramAccountView = NonNullable<InstagramConnectionView["account"]>;
